import { type NotificationRenderContext, renderNotificationText } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { DeleteNotificationCampaignButton } from "../components/DeleteNotificationCampaignButton";
import { ErrorAlert } from "../components/ErrorAlert";
import { LoadMoreButton } from "../components/LoadMoreButton";
import { NotificationCampaignSheet } from "../components/NotificationCampaignSheet";
import { ShortDate } from "../components/ShortDate";
import { StorefrontSection } from "../components/StorefrontSection";
import { formatDate } from "../lib/format";
import {
  campaignAudienceLabel,
  campaignStatusLabel,
  campaignTime,
  isCampaignDue,
  type NotificationCampaign,
} from "../lib/notifications";
import { useTRPC } from "../lib/trpc";
import { useNotificationPageData } from "../lib/useNotifications";

const campaignColumns = createDataTableColumns<NotificationCampaign>();
const CAMPAIGN_PINNED = { start: [], end: ["actions"] };

function buildCampaignColumns(
  context: NotificationRenderContext,
  onOpen: (campaign: NotificationCampaign) => void,
) {
  return campaignColumns.columns([
    campaignColumns.accessor(
      (campaign) => renderNotificationText(campaign.content.title, {}, context),
      {
        id: "title",
        header: "Title",
        size: 280,
        cell: ({ getValue, row }) => (
          <Button
            type="button"
            variant="link"
            className="h-auto max-w-full justify-start p-0 font-medium text-foreground"
            onClick={() => onOpen(row.original)}
          >
            <span className="truncate">{getValue()}</span>
          </Button>
        ),
        meta: { filter: "text" },
      },
    ),
    campaignColumns.accessor((campaign) => campaignAudienceLabel(campaign), {
      id: "audience",
      header: "Audience",
      meta: { filter: "text" },
    }),
    campaignColumns.accessor((campaign) => campaignStatusLabel(campaign, new Date()), {
      id: "status",
      header: "Status",
      cell: ({ getValue }) => (
        <Badge variant={getValue() === "sent" ? "secondary" : "outline"}>{getValue()}</Badge>
      ),
      meta: { filter: "text" },
    }),
    campaignColumns.accessor((campaign) => campaignTime(campaign), {
      id: "time",
      header: "Sends / sent",
      cell: ({ getValue }) => formatDate(getValue()),
    }),
    campaignColumns.accessor("deliveredCount", {
      header: "Delivered",
      cell: ({ getValue }) => {
        const delivered = getValue();
        return delivered === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          delivered.toLocaleString("en-US")
        );
      },
      meta: {
        align: "end",
        filter: "number",
        csv: (value) => (typeof value === "number" ? String(value) : ""),
      },
    }),
    campaignColumns.accessor("createdAt", {
      header: "Created",
      cell: ({ getValue }) => <ShortDate value={getValue()} />,
    }),
    campaignColumns.display({
      id: "actions",
      size: 150,
      cell: ({ row }) =>
        row.original.status === "scheduled" ? (
          <div className="flex justify-end gap-1">
            <Button type="button" variant="outline" size="sm" onClick={() => onOpen(row.original)}>
              Edit
            </Button>
            <DeleteNotificationCampaignButton campaignId={row.original.id} size="sm" />
          </div>
        ) : (
          <div className="flex justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpen(row.original)}>
              View
            </Button>
          </div>
        ),
    }),
  ]);
}

/**
 * Marketing notifications of one environment, newest first, and the composer: one-off sends to
 * every shopper or an uploaded list, now or at a set time. A scheduled one can change until it
 * goes; a sent one is kept to read.
 */
export function NotificationsMarketingPage() {
  const trpc = useTRPC();
  const { templates, storefront } = useNotificationPageData();
  const campaigns = useInfiniteQuery(
    trpc.notifications.campaigns.infiniteQueryOptions(
      { limit: 50 },
      {
        getNextPageParam: (last) => last.nextCursor,
        // A campaign sent now goes out on the dispatcher's next run: refresh until it has.
        refetchInterval: (query) =>
          query.state.data?.pages.some((page) =>
            page.data.some((campaign) => isCampaignDue(campaign, new Date())),
          )
            ? 15_000
            : false,
      },
    ),
  );
  // Which campaign the composer opened (null = a new one), and a key per open for a fresh draft.
  const [composer, setComposer] = useState<{
    key: number;
    campaign: NotificationCampaign | null;
  } | null>(null);
  const [open, setOpen] = useState(false);

  const rows = useMemo(
    () => campaigns.data?.pages.flatMap((page) => page.data) ?? [],
    [campaigns.data],
  );
  const context = templates.data?.context;
  const columns = useMemo(
    () =>
      context
        ? buildCampaignColumns(context, (campaign) => {
            setComposer({ key: Date.now(), campaign });
            setOpen(true);
          })
        : [],
    [context],
  );

  const error = templates.error ?? storefront.error;
  if (error) return <ErrorAlert message={error.message} />;
  if (!context || !storefront.data) return <Skeleton className="h-96" />;

  const { environment, assetsAvailable } = storefront.data;
  const { theme } = storefront.data.config;

  return (
    <StorefrontSection
      title="Marketing"
      description="One-off notifications to every shopper or a list of them, sent now or at a time you pick. Shoppers who turned off news and offers don't get them."
      environment={environment}
      aside={
        <Button
          type="button"
          onClick={() => {
            setComposer({ key: Date.now(), campaign: null });
            setOpen(true);
          }}
        >
          <Plus />
          New notification
        </Button>
      }
    >
      {campaigns.error ? (
        <ErrorAlert message={campaigns.error.message} />
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-3">
            {campaigns.isPending ? (
              <Skeleton className="h-40" />
            ) : (
              <DataTable
                columns={columns}
                data={rows}
                getRowId={(campaign) => campaign.id}
                pinned={CAMPAIGN_PINNED}
                search="Search notifications"
                csvName="marketing-notifications"
                emptyMessage="Nothing sent yet. Write a notification to reach every shopper at once."
              />
            )}
            <LoadMoreButton
              hasNextPage={campaigns.hasNextPage}
              isFetchingNextPage={campaigns.isFetchingNextPage}
              onClick={() => void campaigns.fetchNextPage()}
            />
          </CardContent>
        </Card>
      )}

      <NotificationCampaignSheet
        editorKey={composer?.key ?? null}
        campaign={composer?.campaign ?? null}
        open={open}
        onOpenChange={setOpen}
        context={context}
        theme={theme}
        uploadsAvailable={assetsAvailable}
      />
    </StorefrontSection>
  );
}
